/**
 * Lightweight stack parser for crash locator. No dependencies.
 * Parses stack strings to extract file:line:col; filters out node_modules.
 */

export interface StackFrame {
  /** Raw line */
  raw: string
  /** File path or URL (may be webpack-internal, absolute, or relative) */
  file: string
  line: number
  column: number
  /** Whether this frame is inside node_modules (or typical vendor paths) */
  isVendor: boolean
}

const VENDOR_PATTERNS = [
  /node_modules/i,
  /\/webpack\/runtime\//i,
  /\/next\/dist\//i,
  /\/\.next\//i,
  /^https?:\/\/[^/]+\/_next\/static\//i,
  /\[native code\]/i,
  /^eval /i,
]

/**
 * Check if a file path is from vendor/runtime (not app source).
 */
export function isVendorFrame(file: string): boolean {
  return VENDOR_PATTERNS.some((re) => re.test(file))
}

/**
 * Parse a single stack line. Returns null if line doesn't contain file:line:col.
 * Matches: " at fn (path:line:col)" or " at path:line:col" or " at async path:line:col"
 */
function parseStackLine(line: string): Omit<StackFrame, "isVendor"> | null {
  const trimmed = line.trim()
  // Match "at ... (something:123:45)" or "at ... something:123:45"
  const withParen = /^\s*at\s+(?:async\s+)?(?:.*?\s+\()?\s*([^)]+):(\d+):(\d+)\s*\)?\s*$/
  const noParen = /^\s*at\s+(?:async\s+)?\s*([^:\s]+):(\d+):(\d+)\s*$/
  let file: string
  let lineNum: number
  let col: number
  const m1 = trimmed.match(withParen)
  if (m1) {
    file = m1[1].trim()
    lineNum = parseInt(m1[2], 10)
    col = parseInt(m1[3], 10)
  } else {
    const m2 = trimmed.match(noParen)
    if (!m2) return null
    file = m2[1].trim()
    lineNum = parseInt(m2[2], 10)
    col = parseInt(m2[3], 10)
  }
  if (Number.isNaN(lineNum) || Number.isNaN(col)) return null
  return { raw: line, file, line: lineNum, column: col }
}

/**
 * Parse full error stack into frames. Returns frames in order (top = most recent).
 */
export function parseStack(stack: string | undefined): StackFrame[] {
  if (!stack || typeof stack !== "string") return []
  const lines = stack.split("\n")
  const frames: StackFrame[] = []
  for (const line of lines) {
    const parsed = parseStackLine(line)
    if (!parsed) continue
    const isVendor = isVendorFrame(parsed.file)
    frames.push({ ...parsed, isVendor })
  }
  return frames
}

/**
 * First frame that is not inside node_modules/vendor. Best "suspect" for app code.
 */
export function getSuspectFrame(stack: string | undefined): StackFrame | null {
  const frames = parseStack(stack)
  return frames.find((f) => !f.isVendor) ?? frames[0] ?? null
}

/**
 * Format a frame as "file:line:col" for logging and overlay.
 */
export function formatFrame(frame: StackFrame): string {
  return `${frame.file}:${frame.line}:${frame.column}`
}

/**
 * Check if stack looks like it was already source-mapped (contains app paths like app/, components/, lib/).
 */
export function isStackLikelyMapped(stack: string | undefined): boolean {
  if (!stack) return false
  const hasAppPath = /(?:^|\s)(?:\.\/)?(?:app|components|lib)\//.test(stack) || /\/app\/|\/components\/|\/lib\//.test(stack)
  return hasAppPath
}

/**
 * Extract first chunk URL from stack (e.g. https://.../_next/static/chunks/foo.js:1:2 -> foo.js).
 * Returns base URL for the chunk so overlay can link to chunkUrl + ".map".
 */
export function getFirstChunkUrl(stack: string | undefined): string | null {
  const frames = parseStack(stack)
  const first = frames[0]
  if (!first) return null
  // If file looks like a URL with _next/static/chunks/...js
  const match = first.file.match(/^(https?:\/\/[^?]+?\/_next\/static\/chunks\/[^:]+\.js)(?::|$)/i)
  return match ? match[1] : null
}
